// src/pages/ShippedTransfersPage.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Eye } from "lucide-react";

export default function ShippedTransfersPage() {
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailItems, setDetailItems] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const pharmacyCode = user?.pharmacyCode
    ? String(user.pharmacyCode).replace(/^0+/, "")
    : null;

  const firma = user?.firma || "LEMON";

  // Jednotné formátování exspirace
  const formatExpiration = (value) => {
    if (!value) return "-";
    const clean = String(value).replace(/\. /g, ".");
    const d = new Date(clean);

    if (!isNaN(d)) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    }

    const match = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[3]}.${match[2]}.${match[1]}`;

    return clean;
  };

  // 🔹 Načtení převodek
  useEffect(() => {
    if (!pharmacyCode) return;

    async function fetchTransfers() {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/transfers/shipped/${firma}/${pharmacyCode}`,
          { credentials: "include" }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Chyba načítání");
        setData(json);
      } catch (err) {
        console.error("❌ Chyba načítání převodek:", err);
        setError("Nepodařilo se načíst odeslané převodky.");
      } finally {
        setLoading(false);
      }
    }

    fetchTransfers();
  }, [pharmacyCode, firma]);

  // 🔹 Načtení detailu převodky
  const loadDetail = async (prevodkaId) => {
    try {
      const res = await fetch(`/api/transfers/detail/${prevodkaId}`, {
        credentials: "include",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Chyba načítání detailu");

      setDetailItems(json);
      setSelectedTransfer(prevodkaId);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      alert("Nepodařilo se načíst položky převodky.");
    }
  };

  if (!user) return <div className="p-4">Načítám uživatele…</div>;
  if (loading) return <div className="p-4">Načítám data…</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">
        Odeslané převodky – pobočka {pharmacyCode}
      </h1>

      {data.length > 0 ? (
        <table className="min-w-full border-collapse bg-white shadow rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left">Číslo převodky</th>
              <th className="py-3 px-4 text-left">Cílová pobočka</th>
              <th className="py-3 px-4 text-left">Stav</th>
              <th className="py-3 px-4 text-left">Datum odeslání</th>
              <th className="py-3 px-4 text-left">Akce</th>
            </tr>
          </thead>

          <tbody>
            {data.map((t) => (
              <tr key={t._id} className="border-t hover:bg-gray-50">
                <td className="py-3 px-4">{t.prevodkaId}</td>
                <td className="py-3 px-4">{t.toPharmacyCode}</td>
                <td className="py-3 px-4 capitalize">{t.stav}</td>
                <td className="py-3 px-4">
                  {t.sentAt
                    ? new Date(t.sentAt).toLocaleDateString("cs-CZ")
                    : ""}
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => loadDetail(t.prevodkaId)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={16} /> Detail
                  </button>

                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-gray-500">Žádné odeslané převodky.</div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Položky převodky {selectedTransfer}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <table className="min-w-full border-collapse bg-white">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="py-2 px-3 text-left">PDK</th>
                    <th className="py-2 px-3 text-left">Název</th>
                    <th className="py-2 px-3 text-left">Množství</th>
                    <th className="py-2 px-3 text-left">NCSD</th>
                    <th className="py-2 px-3 text-left w-40">Exspirace</th>
                    <th className="py-2 px-3 text-left">Šarže</th>
                    <th className="py-2 px-3 text-left">Dodavatel</th>
                  </tr>
                </thead>

                <tbody>
                  {detailItems.map((i) => (
                    <tr key={i._id} className="border-t hover:bg-gray-50">
                      <td className="py-2 px-3">{i.PDK}</td>
                      <td className="py-2 px-3">{i.Název}</td>
                      <td className="py-2 px-3">{i.Množství}</td>
                      <td className="py-2 px-3">
                        {i.NCSD ? i.NCSD.toFixed(2) : "-"}
                      </td>
                      <td className="py-2 px-3 w-40">
                        {formatExpiration(i.Exspirace)}
                      </td>
                      <td className="py-2 px-3">{i.Šarže}</td>
                      <td className="py-2 px-3">{i.Dodavatel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
